import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, Share2, Heart, ArrowLeft, ExternalLink, Loader2, AlertCircle, Mail } from "lucide-react";
import Navbar from '../components/Navbar';
import { Footer2 } from '../components/Footer2';
import { supabase } from '../lib/supabase';

// Simple Button component since we're not using shadcn
const Button = ({ variant = "default", size = "default", children, className = "", onClick, ...props }) => {
  const baseClasses = "inline-flex items-center justify-center font-medium transition-all duration-200";
  
  const sizeClasses = {
    default: "px-4 py-2",
    sm: "px-3 py-1 text-sm"
  };
  
  const variantClasses = variant === "outline" 
    ? "border-2 border-black bg-white text-black hover:bg-gray-50 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]"
    : "bg-[#0395fc] text-white border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px]";
  
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses} ${className}`;

  return (
    <button className={combinedClasses} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

const EventTemplate = () => {
  const { slug } = useParams(); // Get slug from URL params
  const navigate = useNavigate();
  
  // State management
  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Format date and time
  const formatEventDateTime = (startDateTime, endDateTime) => {
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    
    const dateOptions = { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
    
    const formattedDate = start.toLocaleDateString('en-US', dateOptions);
    const startTime = start.toLocaleTimeString('en-US', timeOptions);
    const endTime = end.toLocaleTimeString('en-US', timeOptions);
    
    return {
      date: formattedDate,
      time: `${startTime} - ${endTime}`
    };
  };

  // Fetch event details from database
  const fetchEvent = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch event by slug or id
      const { data: eventData, error: fetchError } = await supabase
        .from('events')
        .select(`
          *,
          members!events_created_by_fkey (
            first_name,
            last_name,
            profile_image_url,
            bio
          )
        `)
        .eq('slug', slug)
        .eq('status', 'published')
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          setError('Event not found.');
        } else {
          throw fetchError;
        }
        return;
      }

      if (!eventData) {
        setError('Event not found.');
        return;
      }

      // Set the event data directly
      setEvent(eventData);

    } catch (err) {
      console.error('Error fetching event:', err);
      setError('Failed to load event details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch event on component mount
  useEffect(() => {
    if (slug) {
      fetchEvent();
    } else {
      setError('No event slug provided.');
      setIsLoading(false);
    }
  }, [slug]);

  // Check if event is favorited on mount
  useEffect(() => {
    if (event) {
      const savedEvents = JSON.parse(localStorage.getItem('savedEvents') || '[]');
      setIsFavorited(savedEvents.includes(event.id));
    }
  }, [event]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showShareMenu && !event.target.closest('.share-menu-container')) {
        setShowShareMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  const handleRegister = () => {
    setIsRegistered(!isRegistered);
  };

  const handleFavorite = () => {
    if (!event) return;
    
    const savedEvents = JSON.parse(localStorage.getItem('savedEvents') || '[]');
    
    if (isFavorited) {
      // Remove from saved events
      const updatedEvents = savedEvents.filter(id => id !== event.id);
      localStorage.setItem('savedEvents', JSON.stringify(updatedEvents));
      setIsFavorited(false);
    } else {
      // Add to saved events
      savedEvents.push(event.id);
      localStorage.setItem('savedEvents', JSON.stringify(savedEvents));
      setIsFavorited(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Could not copy link. Please try again.');
    }
  };

  const handleShareToSocial = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(event.title);
    const description = encodeURIComponent(event.description || '');
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${title}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${title}&body=${description}%0A%0A${url}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: event.title,
          text: event.description || '', // Use description instead of content for sharing
          url: window.location.href,
        });
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(window.location.href);
        alert('Event link copied to clipboard!');
      }
    } catch (error) {
      // Handle errors, including user cancellation
      if (error.name === 'AbortError') {
        // User cancelled the share operation
        return;
      }
      console.error('Error sharing:', error);
      // Fallback to copying to clipboard if sharing fails
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Event link copied to clipboard!');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        alert('Could not share or copy the event link. Please try again.');
      }
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#0395fc] mx-auto mb-4" />
            <p className="text-gray-600">Loading event details...</p>
          </div>
        </div>
        <Footer2 />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/events')}>
              Back to Events
            </Button>
          </div>
        </div>
        <Footer2 />
      </div>
    );
  }

  // No event data
  if (!event) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Event Data</h2>
            <p className="text-gray-600 mb-4">Unable to load event information.</p>
            <Button onClick={() => navigate('/events')}>
              Back to Events
            </Button>
          </div>
        </div>
        <Footer2 />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="pt-24 pb-16 bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: event.image_url ? `url(${event.image_url})` : 'url(/5.jpg)' }}
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        <div className="container mx-auto px-4 max-w-7xl relative z-10">
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="outline" 
              size="sm"
              className="bg-white/20 border-white/30 text-white hover:bg-white/30"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Events
            </Button>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <span className="inline-block bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                {event.event_type || 'Event'}
              </span>
              <h1 className="text-4xl md:text-5xl font-black font-[var(--font-anton)] text-white mb-4">
                {event.title}
              </h1>
              <div 
                className="text-xl text-white/90 leading-relaxed prose prose-lg max-w-none text-left"
                dangerouslySetInnerHTML={{ 
                  __html: event.description ? 
                    (event.description.length > 150 ? 
                      event.description.slice(0, 150) + '...' : 
                      event.description
                    ) : ''
                }}
              />
            </div>
            
            {/* Event Info Card */}
            <div className="lg:col-span-1">
              <div className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 overflow-visible">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-[#0395fc]" />
                    <span className="font-medium">
                      {event.start_datetime ? new Date(event.start_datetime).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : 'Date TBA'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-[#0395fc]" />
                    <span>
                      {event.start_datetime ? new Date(event.start_datetime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }) : 'Time TBA'}
                      {event.end_datetime && ` - ${new Date(event.end_datetime).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-[#0395fc]" />
                    <span className="text-sm">{event.location || 'Location TBA'}</span>
                  </div>
                  {event.registration_required && (
                    <>
                      <div className="flex items-center gap-3">
                        <Users className="h-5 w-5 text-[#0395fc]" />
                        <span>
                          {event.current_attendees || 0}/
                          {event.max_attendees || 'Unlimited'} registered
                        </span>
                      </div>
                      
                      {/* Registration Progress */}
                      {event.max_attendees && (
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-[#0395fc] h-2 rounded-full" 
                            style={{ 
                              width: `${Math.min(((event.current_attendees || 0) / event.max_attendees) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4">
                    {event.registration_required && event.registration_link && (
                      <a 
                        href={event.registration_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button 
                          className="w-full font-bold text-lg py-3 border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 bg-[#0395fc] text-white"
                        >
                          Register Now
                        </Button>
                      </a>
                    )}
                    
                    <div className="flex gap-2">
                      
                      <div className="relative flex-1 share-menu-container">
                        <Button 
                          variant="outline"
                          size="sm"
                          className="w-full border-2 border-black"
                          onClick={() => setShowShareMenu(!showShareMenu)}
                        >
                          <Share2 className="h-4 w-4 mr-2" />
                          Share
                        </Button>
                        
                        {/* Share Menu Dropdown */}
                        {showShareMenu && (
                          <div className="absolute top-full mt-2 right-0 w-48 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] z-50">
                            <button
                              onClick={() => handleShareToSocial('twitter')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                              </svg>
                              Share on X
                            </button>
                            <button
                              onClick={() => handleShareToSocial('facebook')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                              </svg>
                              Share on Facebook
                            </button>
                            <button
                              onClick={() => handleShareToSocial('linkedin')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                              </svg>
                              Share on LinkedIn
                            </button>
                            <button
                              onClick={() => handleShareToSocial('email')}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium"
                            >
                              <Mail className="h-4 w-4" />
                              Share via Email
                            </button>
                            <button
                              onClick={handleCopyLink}
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-sm font-medium border-t border-gray-200"
                            >
                              <ExternalLink className="h-4 w-4" />
                              {copySuccess ? '✓ Copied!' : 'Copy Link'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Event Details */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div>
                <h2 className="text-3xl font-black font-[var(--font-anton)] mb-6">About This Event</h2>
                <div 
                  className="prose prose-lg max-w-none text-left leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: event.content || event.description }}
                />
              </div>

              {/* Additional Details */}
              {event.additional_info && (
                <div>
                  <h2 className="text-3xl font-black font-[var(--font-anton)] mb-6">Additional Information</h2>
                  <div className="bg-gray-50 border-4 border-black p-6">
                    <div 
                      className="prose prose-lg max-w-none text-left"
                      dangerouslySetInnerHTML={{ __html: event.additional_info }}
                    />
                  </div>
                </div>
              )}

              {/* Contact Information */}
              {event.contact_info && (
                <div>
                  <h2 className="text-3xl font-black font-[var(--font-anton)] mb-6">Contact Information</h2>
                  <div className="bg-white border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
                    <p className="whitespace-pre-wrap">{event.contact_info}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Event Details Card */}
              <div className="bg-white border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
                <h3 className="text-xl font-bold mb-4">Event Details</h3>
                <div className="space-y-3 text-sm">
                  {event.event_type && (
                    <div>
                      <span className="font-semibold text-gray-600">Type:</span>
                      <span className="ml-2">{event.event_type}</span>
                    </div>
                  )}
                  {event.end_datetime && new Date(event.end_datetime).toDateString() !== new Date(event.start_datetime).toDateString() && (
                    <div>
                      <span className="font-semibold text-gray-600">End Date:</span>
                      <span className="ml-2">
                        {new Date(event.end_datetime).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {event.fee && (
                    <div>
                      <span className="font-semibold text-gray-600">Fee:</span>
                      <span className="ml-2">${event.fee}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Share Event */}
              <div className="bg-white border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] p-6">
                <h3 className="text-xl font-bold mb-4">Share This Event</h3>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-2 border-black"
                    onClick={() => {
                      if (navigator.share) {
                        navigator.share({
                          title: event.title,
                          text: event.description,
                          url: window.location.href
                        });
                      } else {
                        navigator.clipboard.writeText(window.location.href);
                      }
                    }}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer2 />
    </div>
  );
};

export default EventTemplate;